import React, { Component } from "react";
import { connect } from "react-redux";
import * as PropTypes from "prop-types";
import { Link, withRouter } from "react-router-dom";
import { Avatar, Dropdown, Menu, Tooltip } from "antd";

import { authActions } from "./../../redux/actions";
import { SUPPORT_WHATSAPP_URL } from "./../../config/general";
import { buildMenuItems, findActiveKey, findOpenKeys } from "./menuItems";

import "./index.scss";

import packageJson from "./../../../package.json";

const APP_VERSION = process.env.REACT_APP_VERSION || packageJson.version || "";

class Sidebar extends Component {
	static propTypes = {
		mode      : PropTypes.oneOf(["sider", "drawer"]),
		collapsed : PropTypes.bool,
		onToggle  : PropTypes.func,
		onNavigate: PropTypes.func,
	};

	static defaultProps = {
		mode      : "sider",
		collapsed : false,
		onToggle  : () => null,
		onNavigate: () => null,
	};

	constructor(props) {
		super(props);

		this.state = {
			// { [profundidade]: chave do grupo aberto } — accordion por nível
			openKeys: {},
		};
	}

	componentDidMount() {
		this.syncOpenKeysWithRoute();
	}

	componentDidUpdate(prevProps) {
		if( prevProps.location.pathname !== this.props.location.pathname )
		{
			this.syncOpenKeysWithRoute();
		}
	}

	get isCollapsed() {
		return this.props.mode === "sider" && this.props.collapsed;
	}

	get items() {
		return buildMenuItems(this.props.permissions);
	}

	syncOpenKeysWithRoute() {
		const items     = this.items;
		const activeKey = findActiveKey(items, this.props.location.pathname);
		const trail     = activeKey ? findOpenKeys(items, activeKey) : null;

		if( !trail || !trail.length ) return;

		this.setState(({ openKeys }) => ({
			openKeys: trail.reduce((acc, key, depth) => ({ ...acc, [depth]: key }), { ...openKeys }),
		}));
	}

	toggleGroup = (key, depth) => {
		if( this.isCollapsed )
		{
			this.props.onToggle();
		}

		this.setState(({ openKeys }) => {
			const next = {};

			Object.keys(openKeys).forEach((level) => {
				if( Number(level) < depth ) next[level] = openKeys[level];
			});

			if( openKeys[depth] !== key ) next[depth] = key;

			return { openKeys: next };
		});
	};

	renderItem(item, depth, activeKey) {
		const isActive = item.key === activeKey;

		if( item.children )
		{
			return this.renderGroup(item, depth, activeKey);
		}

		const content = (
			<Link
				to={item.to}
				className={`byp-sidebar__item byp-sidebar__item--depth-${depth} ${isActive ? "is-active" : ""}`}
				onClick={this.props.onNavigate}
			>
				{item.icon ? (
					<i className={`byp-sidebar__icon ${item.icon}`} />
				) : (
					<span className="byp-sidebar__bullet" />
				)}
				<span className="byp-sidebar__label">{item.label}</span>
			</Link>
		);

		if( this.isCollapsed )
		{
			return (
				<Tooltip key={item.key} title={item.label} placement="right">
					{content}
				</Tooltip>
			);
		}

		return <React.Fragment key={item.key}>{content}</React.Fragment>;
	}

	renderGroup(item, depth, activeKey) {
		const isOpen     = this.state.openKeys[depth] === item.key;
		const hasActive  = !!findOpenKeys(item.children, activeKey);
		const showChildren = isOpen && !this.isCollapsed;

		const trigger = (
			<button
				type="button"
				className={`byp-sidebar__item byp-sidebar__item--group byp-sidebar__item--depth-${depth} ${isOpen ? "is-open" : ""} ${hasActive ? "has-active" : ""}`}
				onClick={() => this.toggleGroup(item.key, depth)}
				aria-expanded={showChildren}
			>
				{item.icon ? (
					<i className={`byp-sidebar__icon ${item.icon}`} />
				) : (
					<span className="byp-sidebar__bullet" />
				)}
				<span className="byp-sidebar__label">{item.label}</span>
				<i className="byp-sidebar__arrow far fa-chevron-down" />
			</button>
		);

		return (
			<div className="byp-sidebar__group" key={item.key}>
				{this.isCollapsed ? (
					<Tooltip title={item.label} placement="right">{trigger}</Tooltip>
				) : trigger}

				{showChildren && (
					<div className="byp-sidebar__submenu">
						{item.children.map((child) => this.renderItem(child, depth + 1, activeKey))}
					</div>
				)}
			</div>
		);
	}

	renderUserMenu() {
		const menu = (
			<Menu>
				<Menu.Item key="account" title={false}>
					<Link to="/account">Meus dados</Link>
				</Menu.Item>
				<Menu.Item key="password" title={false}>
					<Link to="/account/password">Alterar senha</Link>
				</Menu.Item>
				<Menu.Divider />
				<Menu.Item key="logout" title={false} onClick={() => this.props.doLogout()}>
					<span>Sair</span>
				</Menu.Item>
			</Menu>
		);

		const { name, avatar, roles } = this.props.user;
		const role = roles && roles.length ? roles[0].name : "Usuário";

		return (
			<Dropdown overlay={menu} placement="topRight" trigger={["click"]}>
				<button type="button" className="byp-sidebar__user">
					{avatar
						? <Avatar size={36} src={avatar} />
						: <Avatar size={36} icon={<i className="fad fa-user" />} />}
					<span className="byp-sidebar__user-info">
						<strong>{name}</strong>
						<small>{role}</small>
					</span>
					<i className="byp-sidebar__arrow far fa-chevron-down" />
				</button>
			</Dropdown>
		);
	}

	render() {
		const items      = this.items;
		const activeKey  = findActiveKey(items, this.props.location.pathname);
		const collapsed  = this.isCollapsed;

		return (
			<aside className={`byp-sidebar ${collapsed ? "byp-sidebar--collapsed" : ""} byp-sidebar--${this.props.mode}`}>
				<div className="byp-sidebar__header">
					<Link to="/" className="byp-sidebar__brand" onClick={this.props.onNavigate}>
						<span className="byp-sidebar__brand-lockup">
							<img
								className="byp-sidebar__brand-mark"
								src="/images/logos/byponto-mark.png"
								alt="ByPonto"
							/>
							<img
								className="byp-sidebar__brand-wordmark"
								src="/images/logos/byponto-wordmark.png"
								alt=""
							/>
						</span>
						<small className="byp-sidebar__brand-caption">Painel administrativo</small>
					</Link>
				</div>

				<nav className="byp-sidebar__nav">
					{items.map((item) => this.renderItem(item, 0, activeKey))}
				</nav>

				<div className="byp-sidebar__footer">
					<a
						href={SUPPORT_WHATSAPP_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="byp-sidebar__help"
						onClick={this.props.onNavigate}
					>
						<span className="byp-sidebar__help-icon"><i className="far fa-question" /></span>
						<span className="byp-sidebar__help-text">
							<strong>Precisa de ajuda?</strong>
							<small>Fale com nosso suporte</small>
						</span>
						<i className="byp-sidebar__arrow far fa-chevron-right" />
					</a>

					{this.renderUserMenu()}

					{!!APP_VERSION && !collapsed && (
						<p className="byp-sidebar__version">Versão {APP_VERSION}</p>
					)}
				</div>
			</aside>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		permissions: state.auth.userData.permissions,
		user       : state.auth.userData,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		doLogout: () => dispatch(authActions.logout()),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Sidebar));
